import { Observable, map } from "rxjs";
import HttpService from "./HttpService";
import { url } from "./APIEndPoints";  
import { buildParamString, IAPIResponse } from "@mindmemobile/mm-shared-global";
import { CreateProductResponse, GetProductAttributesResponseModel } from "../models/ProductAPIResponse.model";
import { IAPICreateUpdateProductModel, IAPIGetEditProductModel, IAPIProductAttributesResponseModel, IAPIProductGridResponse, IAPIProductReferenceInPurchaseResponse } from "../interfaces/IAPIProductModels";
import { IAPIUpdateVariantModel } from "../interfaces/IAPIVariantModels";

class ProductService {
  private httpService: HttpService;

  constructor() {
    this.httpService = new HttpService(
      undefined,
      `${process.env.REACT_APP_API_KEY}`,
      process.env.REACT_APP_API_BASE_URL
    );
  }

  saveProduct(
    data: IAPICreateUpdateProductModel
  ): Observable<CreateProductResponse> {
    return this.httpService
      .post<IAPIResponse<CreateProductResponse>>(url.createProduct, data)
      .pipe(map((result) => new CreateProductResponse(result.result)));
  }

  updateProduct(data: IAPICreateUpdateProductModel): Observable<boolean> {
    return this.httpService
      .put<IAPIResponse<boolean>>(url.updateProduct, data)
      .pipe(map((result) => result.result));
  }

  getAllAttribues(
    accountId: string
  ): Observable<GetProductAttributesResponseModel[]> {
    const params: string = buildParamString({
      accountId: accountId,
    });

    return this.httpService
      .get<
        IAPIResponse<IAPIProductAttributesResponseModel[]>
      >(url.getAllAttributes, params)
      .pipe(
        map((result) =>
          GetProductAttributesResponseModel.deserializeList(result.result)
        )
      );
  }

  getProduct(
    accountId: string,
    productId: string
  ): Observable<IAPIResponse<IAPIGetEditProductModel>> {
    const params: string = buildParamString({
      accountId: accountId,
      productId: productId,
    });

    return this.httpService.get<IAPIResponse<IAPIGetEditProductModel>>(
      url.getProduct,
      params
    );
  }

  getItems(
    accountId: string,
    pageNumber: number,
    pageSize: number,
    searchValue: string
  ): Observable<IAPIResponse<IAPIProductGridResponse[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchValue: searchValue,
    });

    return this.httpService.get<IAPIResponse<IAPIProductGridResponse[]>>(
      url.getAllItems,
      params
    );
  }

  updateVariant(data: IAPIUpdateVariantModel): Observable<boolean> {
    return this.httpService
      .put<IAPIResponse<boolean>>(url.updateVariant, data)
      .pipe(map((result) => result.result));
  }

  updateInventorySettings(
    accountId: string,
    productId: string,
    onHandQuantity: number,
    isInStock: boolean
  ): Observable<IAPIResponse<boolean>> {
    const request = {
      accountId: accountId,
      productId: productId,
      available: onHandQuantity,
      isInStock: isInStock,
    };

    return this.httpService.put<IAPIResponse<boolean>>(
      url.updateInventorySettings,
      request
    );
  }

  updateStatus(
    accountId: string,
    id: string,
    isActive: boolean
  ): Observable<IAPIResponse<boolean>> {
    const request = {
      accountId: accountId,
      id: id,
      isActive: isActive,
    };

    return this.httpService.put<IAPIResponse<boolean>>(
      url.productStatusUpdate,
      request
    );
  }

  deleteItem(accountId: string, itemId: string) {
    const request = {
      accountId: accountId,
      itemId: itemId,
    };

    return this.httpService.put<IAPIResponse<boolean>>(
      url.deleteProduct,
      request
    );
  }

  getProductReferenceInPurchases(
    accountId: string,
    id: string
  ): Observable<IAPIResponse<IAPIProductReferenceInPurchaseResponse>> {
    const params: string = buildParamString({
      accountId: accountId,
      id: id,
    });

    return this.httpService.get<
      IAPIResponse<IAPIProductReferenceInPurchaseResponse>
    >(url.getProductReferenceInPurchase, params);
  }
}

export default new ProductService();
